import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { getMyConsents, grantConsent, ConsentRecord } from '../services/dataPrivacyApi';

// ---------------------------------------------------------------------------
// POPIA Consent Banner (Phase 9)
// ---------------------------------------------------------------------------
// Shows a banner on first login / when consent is missing, asking the user
// to accept required consents (terms, privacy, data_processing).
// Marketing consent is opt-in and shown separately.
//
// The banner only renders for authenticated users who are missing required
// consents. Once all required consents are granted, the banner is hidden.
// ---------------------------------------------------------------------------

const CONSENT_VERSION = '1.0';

const REQUIRED_CONSENTS = ['terms', 'privacy', 'data_processing'] as const;

export default function ConsentBanner() {
  const user = useSelector((state: RootState) => state.auth.user);
  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    getMyConsents()
      .then((records) => {
        setConsents(records);
        // Check if any required consents are missing
        const grantedTypes = new Set(
          records.filter((r) => r.granted).map((r) => r.consentType),
        );
        const hasMissing = REQUIRED_CONSENTS.some((t) => !grantedTypes.has(t));
        setVisible(hasMissing);
      })
      .catch(() => {
        // If we can't fetch consents, don't block the user
        setVisible(false);
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (loading || !visible || !user) return null;

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      // Grant all required consents
      const grantedTypes = new Set(
        consents.filter((r) => r.granted).map((r) => r.consentType),
      );

      for (const consentType of REQUIRED_CONSENTS) {
        if (!grantedTypes.has(consentType)) {
          await grantConsent(consentType, CONSENT_VERSION);
        }
      }

      // Optionally grant marketing consent
      if (marketingOptIn) {
        await grantConsent('marketing', CONSENT_VERSION);
      }

      setVisible(false);
    } catch {
      // Don't block the user on error — they can manage consent later
      setVisible(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#1e293b',
        color: '#f8fafc',
        padding: '1.25rem 2rem',
        zIndex: 9999,
        boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <strong>Privacy & Consent</strong>
          <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
            By using ReCurr, you agree to our Terms of Service, Privacy Policy, and consent to the processing of your
            personal data in accordance with the Protection of Personal Information Act (POPIA).
          </p>
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
            <input
              type="checkbox"
              checked={marketingOptIn}
              onChange={(e) => setMarketingOptIn(e.target.checked)}
            />
            I also consent to receiving marketing communications (optional)
          </label>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            onClick={handleAccept}
            disabled={submitting}
            style={{
              padding: '0.5rem 1.5rem',
              backgroundColor: '#1e2c4f',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
          >
            {submitting ? 'Saving...' : 'Accept & Continue'}
          </button>
          <a
            href="/settings/privacy"
            style={{ color: '#94a3b8', fontSize: '0.875rem', textDecoration: 'underline' }}
          >
            Manage preferences
          </a>
        </div>
      </div>
    </div>
  );
}
